/* eslint-disable no-restricted-syntax */
/* eslint-disable no-use-before-define */
import Compress from 'compress.js';
import {
  endTaskDialogInitialState,
  errorMessages,
  devicesValidationTicketTypes,
} from '../constants';
import {
  ticketTypesForInstallationProtocol,
  ticketTypesForTroubleProtocol,
} from '../Tasks/components/Documents/constants';
import {
  setSnackbar,
  setData,
  setEndTaskDialog,
  setTaskStarted,
  setTaskVerifyAddress,
  setClosedTaskUsedMaterials,
  setNewUsedMaterials,
  setConfirmationDialog,
  setAutomaticTestCloseReasons,
  setAutomaticTestResults,
  setRejectSignatureRequestDialog,
  updateMaterialsToSell,
  setMaterialsDialog,
  setReturnedMaterials,
  setReturnMaterialsDialog,
} from '../Tasks/redux';

const usedMaterialsListType = 'usedMaterials';
const newUsedMaterialsListType = 'newUsedMaterials';
const closedTaskUsedMaterialsListType = 'closedTaskUsedMaterials';

// Chrome 148 camera captures trigger ERR_UPLOAD_FILE_CHANGED because Android's
// camera post-processes the file (EXIF, rotation) after handing it to the browser,
// changing its modification time. Chrome detects this when streaming the file for
// the multipart upload and aborts with ERR_UPLOAD_FILE_CHANGED.
//
// Fix: convert every Blob/File to a pure in-memory Blob backed by an ArrayBuffer
// before it touches FormData. An ArrayBuffer-backed Blob has no disk reference
// and Chrome cannot throw ERR_UPLOAD_FILE_CHANGED for it.
//
// We use URL.createObjectURL + fetch instead of FileReader because FileReader can
// hang on certain camera captures in Chrome 148 (different code path issue).
// ERR_UPLOAD_FILE_CHANGED fires when Chrome's network stack reads a disk-backed
// File whose mtime changed (camera post-processing). It propagates to every
// network-type read: FormData upload, fetch(blobUrl), XHR, etc.
//
// file.arrayBuffer() is a Blob API call — it does NOT go through the network
// stack and is NOT subject to the file-change detection. Converting to an
// ArrayBuffer-backed Blob therefore breaks the chain to the original disk file.
// Decodes a Blob/File via an <img> element → canvas → canvas.toBlob().
// This path goes through Chrome's image-rendering pipeline, NOT the network/upload
// stack, so it is immune to ERR_UPLOAD_FILE_CHANGED and FileReader hangs that
// Chrome 148 introduced for camera-captured files whose mtime changes during
// post-processing.
const toMemoryViaCanvas = f =>
  new Promise((resolve, reject) => {
    const blobUrl = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      canvas.toBlob(
        blob => {
          if (!blob) { reject(new Error('canvas.toBlob returned null')); return; }
          const name = (f instanceof File && f.name) || 'image.jpg';
          resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
        },
        'image/jpeg',
        0.95
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error('image decode failed'));
    };
    img.src = blobUrl;
  });

const toMemoryFile = async (f, fallbackName) => {
  if (!(f instanceof Blob)) return f;
  const name = (f instanceof File && f.name) || fallbackName || 'image.jpg';
  const type = f.type || 'image/jpeg';

  // data URL path: no disk access at all
  if (f.src && typeof f.src === 'string' && f.src.startsWith('data:')) {
    const r = await fetch(f.src);
    const buf = await r.arrayBuffer();
    return new File([new Blob([buf], { type })], name, { type, lastModified: Date.now() });
  }

  // Canvas path: decodes via image rendering pipeline — not the network/upload stack.
  // Immune to ERR_UPLOAD_FILE_CHANGED and FileReader/arrayBuffer() hangs.
  try {
    return await Promise.race([
      toMemoryViaCanvas(f),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('canvas timeout after 15s')), 15000);
      }),
    ]);
  } catch (e) { /* fall through to arrayBuffer */ }

  // Fallback: arrayBuffer() — works for gallery/non-camera files
  try {
    const buf = await Promise.race([
      f.arrayBuffer(),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('arrayBuffer timeout after 10s')), 10000);
      }),
    ]);
    return new File([new Blob([buf], { type })], name, { type, lastModified: Date.now() });
  } catch (e) {
    throw new Error('FILE_UNREADABLE');
  }
};

const compressImages = async files => {
  const compress = new Compress();

  // Convert all input files to pure in-memory Files before compress.js touches them.
  const normalizedFiles = await Promise.all(
    files.map(async (f, idx) => {
      const fallback = (f && (f.name || f.title)) || `image_${idx}.jpg`;
      try {
        return await toMemoryFile(f, fallback);
      } catch (e) {
        if (e.message === 'FILE_UNREADABLE') throw e;
        return f;
      }
    })
  );

  const data = await Promise.race([
    compress.compress(normalizedFiles, {
      size: 4,
      quality: 0.75,
      maxWidth: 1920,
      maxHeight: 1920,
      resize: true,
    }),
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('COMPRESS_TIMEOUT')), 20000);
    }),
  ]);

  return data.map((item, i) => {
    const originalFile = normalizedFiles[i];
    if (!item || !item.data) return originalFile;
    const blob = Compress.convertBase64ToFile(item.data);
    return new File([blob], (originalFile instanceof File && originalFile.name) || 'image.jpg', {
      type: blob.type || 'image/jpeg',
      lastModified: Date.now(),
    });
  });
};

const getTime = input => {
  let result = input;
  if (input && input !== '?') {
    result = `${`0${input.hours}`.slice(-2)}:${`0${input.minutes}`.slice(-2)}`;
  }
  return result;
};

const groupMaterialsByName = materials => {
  const result = [];
  for (let i = 0; i < materials.length; i++) {
    const propName = materials[i].materialName;
    const prop = result.find(x => Object.keys(x).includes(propName));
    if (prop) {
      const updateProp = { [propName]: prop[propName] + materials[i].quantity };
      result.splice(result.indexOf(prop), 1, updateProp);
    } else {
      const newProp = { [propName]: materials[i].quantity };
      result.push(newProp);
    }
  }

  return result;
};

const getTaskTypeName = typeId => {
  switch (typeId) {
    case 1:
    case 4:
      return 'свободно';
    case 2:
      return 'почивка';
    case 5:
      return 'материали';
    default:
      return '';
  }
};

const getTimeSpan = miliseconds => {
  const totalSeconds = Math.floor(miliseconds / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = totalHours / 24;
  // const seconds = totalSeconds % 60;
  const minutes = totalMinutes % 60;
  const hours = totalHours % 24;

  return {
    ticks: miliseconds * 10000,
    days: 0,
    hours,
    miliseconds: 0,
    minutes,
    seconds: 0,
    totalDays,
    totalHours,
    totalMilliseconds: miliseconds,
    totalMinutes,
    totalSeconds,
  };
};

const asyncFetch = (url, options, headers, onError, dispatch) => {
  const token = localStorage.getItem('OSS_Token');
  if (!token) {
    return undefined;
  }

  const header = {
    headers: new Headers({
      ...headers,
      accept: 'application/json, application/xml, text/plain, text/html, *.*',
      authorization: `OSS ${token}`,
    }),
  };

  const fetchOptions = { ...header, ...options };

  const aFetch = async () => {
    const response = await fetch(url, fetchOptions);
    if (response.status < 200 && response.status >= 300) {
      onError
        ? onError()
        : dispatch &&
          dispatch(
            setSnackbar({
              open: true,
              message: errorMessages.notOk,
            })
          );
    }

    const data = await response.json();

    // 500
    if (data.error && data.stackTrace) {
      fetch(`${process.env.REACT_APP_TECHNICIANS_API}/Tasks/Log`, {
        ...{
          headers: new Headers({
            'Content-Type': 'application/json',
            accept: 'application/json, application/xml, text/plain, text/html, *.*',
            authorization: `OSS ${token}`,
          }),
        },
        ...{
          method: 'POST',
          mode: 'cors',
          referrerPolicy: 'strict-origin-when-cross-origin',
          body: JSON.stringify({
            logMessage: `${data.error.join(', ')} ${data.stackTrace
              .match(/\\\w*.cs:line [0-9]*/gm)
              .join(', ')}`,
          }),
        },
      });

      dispatch(
        setSnackbar({
          open: true,
          message: `Възникна системна грешка`,
        })
      );
    }

    // else
    if (data.title && data.status) {
      fetch(`${process.env.REACT_APP_TECHNICIANS_API}/Tasks/Log`, {
        ...{
          headers: new Headers({
            'Content-Type': 'application/json',
            accept: 'application/json, application/xml, text/plain, text/html, *.*',
            authorization: `OSS ${token}`,
          }),
        },
        ...{
          method: 'POST',
          mode: 'cors',
          referrerPolicy: 'strict-origin-when-cross-origin',
          body: JSON.stringify({
            logMessage: `${data.status}, ${data.title}`,
          }),
        },
      });
    }

    return data;
  };

  return aFetch().catch(x => {
    if (x.message && x.message.includes('fetch')) {
      onError
        ? onError()
        : dispatch &&
          dispatch(
            setSnackbar({
              open: true,
              message: 'Неуспешна интернет връзка със сървъра',
            })
          );
    }
  });
};

const formatDateTime = date => {
  return `${`0${date.getHours()}`.slice(-2)}:${`0${date.getMinutes()}`.slice(-2)}`;
};

const formatDate = date => {
  return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
};

const sortByDate = (firstTask, secondTask) => {
  if (firstTask.start.ticks !== secondTask.start.ticks) {
    return firstTask.start.ticks - secondTask.start.ticks;
  }
  return firstTask.end.ticks - secondTask.end.ticks;
};

const removeUsedMaterial = (
  usedMaterial,
  materialList,
  materialListType,
  originalData,
  dispatch
) => {
  const material = materialList.find(
    x =>
      x.materialId === usedMaterial.materialId &&
      x.serialNumber === usedMaterial.serialNumber &&
      x.materialName === usedMaterial.materialName
  );
  if (material) {
    const updatedUsedMaterials = [...materialList];
    updatedUsedMaterials.splice(updatedUsedMaterials.indexOf(material), 1);

    switch (materialListType) {
      case usedMaterialsListType:
        dispatch(setData({ ...originalData, usedMaterials: updatedUsedMaterials }));
        break;
      case newUsedMaterialsListType:
        dispatch(setNewUsedMaterials(updatedUsedMaterials));
        break;
      case closedTaskUsedMaterialsListType:
        dispatch(setClosedTaskUsedMaterials(updatedUsedMaterials));
        break;
      default:
        break;
    }

    return updatedUsedMaterials;
  }
  return undefined;
};

const deleteUsedMaterial = (usedMaterial, data, dispatch) => {
  (async () => {
    const response = await asyncFetch(
      `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/delete-invested-materials`,
      {
        method: 'POST',
        mode: 'cors',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify({
          taskId: usedMaterial.taskId,
          materialId: usedMaterial.materialId,
          serialNumber: usedMaterial.serialNumber,
        }),
      },
      {
        'Content-Type': 'application/json',
      },
      undefined,
      dispatch
    );

    if (response && response.isSuccess) {
      dispatch(setSnackbar({ open: true, message: 'Успешно изтрит материал' }));
      const updatedMaterials = [...data.materials];
      const material = updatedMaterials.find(
        x =>
          x.materialId === usedMaterial.materialId &&
          x.serialNumber === usedMaterial.serialNumber &&
          x.materialName === usedMaterial.materialName
      );
      if (material) {
        const updatedMaterial = { ...material };
        updatedMaterial.quantity += usedMaterial.quantity;
        updatedMaterials.splice(updatedMaterials.indexOf(material), 1, updatedMaterial);
        dispatch(setData({ ...data, materials: updatedMaterials }));
      }
    }
    if (response && response.error) {
      dispatch(setSnackbar({ open: true, message: response.error }));
    }
  })();
};

const addUsedMaterial = (
  quantity,
  material,
  taskId,
  taskAddress,
  newUsedMaterials,
  taskAppEnd,
  closedTaskUsedMaterials,
  dispatch
) => {
  if (taskAppEnd) {
    const updatedClosedTaskUsedMaterials = [...closedTaskUsedMaterials];
    const closedTaskUsedMaterial = closedTaskUsedMaterials.find(
      x =>
        x.materialId === material.materialId &&
        x.materialName === material.materialName &&
        x.serialNumber === material.serialNumber
    );
    if (closedTaskUsedMaterial) {
      updatedClosedTaskUsedMaterials.splice(
        updatedClosedTaskUsedMaterials.indexOf(closedTaskUsedMaterial),
        1,
        {
          ...closedTaskUsedMaterial,
          quantity,
        }
      );
    } else {
      updatedClosedTaskUsedMaterials.push({ ...material, taskAddress, taskId, quantity });
    }
    dispatch(setClosedTaskUsedMaterials(updatedClosedTaskUsedMaterials));
  } else {
    const updatedNewUsedMaterials = [...newUsedMaterials];
    const newUsedMaterial = newUsedMaterials.find(
      x =>
        x.materialId === material.materialId &&
        x.materialName === material.materialName &&
        x.serialNumber === material.serialNumber
    );
    if (newUsedMaterial) {
      updatedNewUsedMaterials.splice(updatedNewUsedMaterials.indexOf(newUsedMaterial), 1, {
        ...newUsedMaterial,
        quantity,
      });
    } else {
      updatedNewUsedMaterials.push({ ...material, taskAddress, taskId, quantity });
    }
    dispatch(setNewUsedMaterials(updatedNewUsedMaterials));
  }
};

const addReturnedMaterial = (quantity, material, returnedMaterials, dispatch) => {
  const returnedMaterial = returnedMaterials.find(
    x => x.materialId === material.materialId && x.materialName === material.materialName
  );

  const updatedReturnedMaterials = [...returnedMaterials];
  if (returnedMaterial) {
    updatedReturnedMaterials.splice(updatedReturnedMaterials.indexOf(returnedMaterial), 1, {
      ...returnedMaterial,
      quantity,
    });
  } else {
    updatedReturnedMaterials.push({ ...material, quantity });
  }

  dispatch(setReturnedMaterials(updatedReturnedMaterials));
};

const removeReturnedMaterial = (material, returnedMaterials, dispatch) => {
  const returnedMaterial = returnedMaterials.find(
    x => x.materialId === material.materialId && x.materialName === material.materialName
  );
  if (returnedMaterial) {
    const updatedReturnedMaterials = [...returnedMaterials];
    updatedReturnedMaterials.splice(updatedReturnedMaterials.indexOf(returnedMaterial), 1);
    dispatch(setReturnedMaterials(updatedReturnedMaterials));
  }
};

const startTask = (data, task, dispatch) => {
  (async () => {
    const response = await asyncFetch(
      `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/Start`,
      {
        method: 'POST',
        mode: 'cors',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify({
          taskId: task.id,
        }),
      },
      {
        'Content-Type': 'application/json',
      },
      undefined,
      dispatch
    );

    if (response) {
      if (response.isSuccess) {
        const updatedTasks = [...data.tasks];
        updatedTasks.splice(data.tasks.indexOf(task), 1, {
          ...task,
          start: response.startTime,
          appStart: { hasValue: true, value: response.startTime },
        });

        const updatedSchedule = { ...data.scheduleByDate };
        updatedSchedule.techniciansData = response.techniciansData;

        dispatch(
          setData({
            ...data,
            tasks: updatedTasks.sort((a, b) => sortByDate(a, b)),
            scheduleByDate: updatedSchedule,
          })
        );
        dispatch(setTaskStarted({ started: true, taskId: task.id }));

        dispatch(setSnackbar({ open: true, message: 'Задачата е започната' }));
      } else {
        switch (response.error) {
          case 'taskStarted':
            dispatch(setData({ ...data, loaded: false }));
            dispatch(setSnackbar({ open: true, message: 'Задачата е била започната вече' }));
            break;
          case 'anotherTaskStarted':
            dispatch(setData({ ...data, loaded: false }));
            dispatch(
              setSnackbar({ open: true, message: 'Друга задача в графика е била започната вече' })
            );
            break;
          case 'movedToAnotherSchedule':
            dispatch(setData({ ...data, loaded: false }));
            dispatch(setSnackbar({ open: true, message: 'Задачата е преместена в друг график' }));
            break;
          default:
            response.error && dispatch(setSnackbar({ open: true, message: response.error }));
            break;
        }
      }
    }
  })();
};

const endTask = async ({
  data,
  taskId,
  closeStatus,
  itsActivityType,
  isTax,
  taxHours,
  taxMinutes,
  taxNote,
  itsActivityDescription,
  closeNote,
  closeReason,
  breakdownCloseReason,
  imageInputData,
  newUsedMaterials,
  dispatch,
  endTaskDialog,
  automaticTestCloseReasonId,
  automaticTestNote,
  videoSurvImageInputData,
  serviceOnAddress,
  documentsDashboard,
  isContractSignatureRequestRejectedWithReason,
  rejectContractSignatureReasonId,
  rejectContractSignatureRequestNote,
  isProtocolSignatureRequestRejectedWithReason,
  rejectProtocolSignatureReasonId,
  rejectProtocolSignatureRequestNote,
  sellMaterialsData,
  ticketCloseReasons,
}) => {
  dispatch(setEndTaskDialog({ ...endTaskDialog, isEnding: true }));
  const taskStatus = closeReason || closeStatus;
  const taskIndex = data.tasks.indexOf(data.tasks.find(x => x.id === taskId));
  const task = data.tasks[taskIndex];
  const formData = new FormData();

  formData.append(
    'taskChanges',
    JSON.stringify({
      taskId: task.id,
      noteClosing: closeNote,
      status: taskStatus,
      itsActivityType,
      isTax,
      taxHours,
      taxMinutes,
      taxNote,
      itsActivityDescription,
      breakdownReason: breakdownCloseReason,
      technology: task.technology,
      networkParameter1: '',
      networkParameter2: '',
      materials: newUsedMaterials,
      serviceOnAddress,
      isAutomaticTestsProceeded: task.isAutomaticTestsProceeded,
      automaticTestCloseReasonId,
      automaticTestNote,
      isContractSignatureRequestRejectedWithReason,
      rejectContractSignatureReasonId,
      rejectContractSignatureRequestNote,
      isProtocolSignatureRequestRejectedWithReason,
      rejectProtocolSignatureReasonId,
      rejectProtocolSignatureRequestNote,
      sellMaterialsData,
      msisdn: task.msisdn,
      ticketCloseReasons: ticketCloseReasons,
    })
  );

  if (imageInputData) {
    const imageDataWithValues = imageInputData.filter(x => x.value !== null);
    let compressedImages;
    try {
      compressedImages = await compressImages(imageDataWithValues.map(x => x.value));
    } catch (e) {
      if (e.message === 'FILE_UNREADABLE' || e.message === 'COMPRESS_TIMEOUT') {
        dispatch(setSnackbar({
          open: true,
          message: 'Снимката не може да бъде прочетена. Натисни ⋮ → Browse... и избери снимката от галерията.',
        }));
        dispatch(setEndTaskDialog({ ...endTaskDialog, isEnding: false }));
        return;
      }
      compressedImages = await Promise.all(
        imageDataWithValues.map(async (x, idx) => {
          try {
            return await toMemoryFile(x.value, `image_${idx}.jpg`);
          } catch (e2) {
            return x.value;
          }
        })
      );
    }
    for (let i = 0; i < imageDataWithValues.length; i++) {
      if (compressedImages[i]) {
        formData.append(imageDataWithValues[i].label, compressedImages[i]);
      }
    }
  }
  if (videoSurvImageInputData) {
    const videoImageDataWithValues = videoSurvImageInputData.filter(x => x.value !== null);
    await Promise.all(
      videoImageDataWithValues.map(async item => {
        try {
          const memFile = await toMemoryFile(item.value, item.label);
          formData.append(item.label, memFile);
        } catch (e) {
          formData.append(item.label, item.value);
        }
      })
    );
  }
  (async () => {
    const response = await asyncFetch(
      `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/End`,
      {
        method: 'POST',
        mode: 'cors',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: formData,
      },
      undefined,
      undefined,
      dispatch
    );

    if (
      response?.isSuccess ||
      response?.error === 'Възникна системна грешка при приключването на задачата'
    ) {
      if (
        response?.error ===
        'Нужно е да има минимум 5 минути разлика между началото и края на задачата'
      ) {
        dispatch(
          setSnackbar({
            open: true,
            message: response?.error,
          })
        );
        dispatch(
          setEndTaskDialog({
            ...endTaskDialog,
            isEnding: false,
          })
        );
        return;
      }

      const updatedTasks = [...data.tasks];
      updatedTasks.splice(taskIndex, 1, {
        ...task,
        end: response.endTime,
        appEnd: { hasValue: true, value: response.endTime },
        status: taskStatus,
        noteClosing: closeNote,
      });

      if (data.materials) {
        const updatedMaterials = [...data.materials];
        for (let i = 0; i < newUsedMaterials.length; i++) {
          const material = data.materials.find(
            x =>
              x.materialId === newUsedMaterials[i].materialId &&
              x.serialNumber === newUsedMaterials[i].serialNumber &&
              x.materialName === newUsedMaterials[i].materialName
          );
          if (material) {
            const materialIndex = updatedMaterials.indexOf(material);
            updatedMaterials.splice(materialIndex, 1, {
              ...material,
              quantity: material.quantity - newUsedMaterials[i].quantity,
            });
          }
        }

        dispatch(
          setData({
            ...data,
            tasks: updatedTasks.sort((a, b) => sortByDate(a, b)),
            materials: updatedMaterials,
            usedMaterials: [...data.usedMaterials, ...newUsedMaterials],
          })
        );
      }

      dispatch(updateMaterialsToSell([]));
      dispatch(setNewUsedMaterials([]));
      dispatch(setTaskStarted({ started: false, taskId: null }));
      dispatch(setEndTaskDialog(endTaskDialogInitialState));
      response?.error === 'Възникна системна грешка при приключването на задачата' &&
      process.env.REACT_APP_ENV !== 'Prod'
        ? dispatch(
            setSnackbar({
              open: true,
              message: 'Задачата е приключена но bbs2 save method се счупи',
            })
          )
        : dispatch(setSnackbar({ open: true, message: 'Задачата е приключена' }));
    } else {
      const isContractSignatureRequestPending = response?.IsContractSignatureRequestPending;

      // todo да се тества
      if (isContractSignatureRequestPending) {
        const updatedTasks = [...data.tasks];
        updatedTasks.splice(taskIndex, 1, {
          ...task,
          documentsDashboard: { ...documentsDashboard, isContractSignatureRequestPending: true },
        });

        dispatch(
          setData({
            ...data,
            tasks: updatedTasks.sort((a, b) => sortByDate(a, b)),
          })
        );
        const result1 = [];
        result1.push({
          text: `Изберете причина за отказ на дигитално подписване на документи`,
          fontSize: '14px',
        });

        dispatch(
          setRejectSignatureRequestDialog({
            open: true,
            title: result1,
            onConfirm: (isRequestRejectedWithReason, rejectSignatureRequestDialog) => {
              endTask({
                data: { ...data },
                taskId,
                closeStatus,
                itsActivityType,
                isTax,
                taxHours,
                taxMinutes,
                taxNote,
                itsActivityDescription,
                closeNote,
                closeReason,
                breakdownCloseReason,
                imageInputData,
                newUsedMaterials,
                dispatch,
                endTaskDialog,
                documentsDashboard,
                isContractSignatureRequestRejectedWithReason: isRequestRejectedWithReason,
                rejectContractSignatureReasonId:
                  rejectSignatureRequestDialog.rejectSignatureRequestReasonId,
                rejectContractSignatureRequestNote:
                  rejectSignatureRequestDialog.rejectSignatureRequestNote,
                sellMaterialsData,
              });
            },
            onCancel: () => {
              dispatch(
                setConfirmationDialog({
                  open: false,
                  title: [],
                  onConfirm: null,
                  onCancel: null,
                })
              );
            },
          })
        );
      }

      const isProtocolSignatureRequestPending = response?.isProtocolSignatureRequestPending;

      if (isProtocolSignatureRequestPending) {
        const updatedTasks = [...data.tasks];
        updatedTasks.splice(taskIndex, 1, {
          ...task,
          documentsDashboard: { ...documentsDashboard, isProtocolSignatureRequestPending: true },
        });

        dispatch(
          setData({
            ...data,
            tasks: updatedTasks.sort((a, b) => sortByDate(a, b)),
          })
        );
        const result1 = [];
        result1.push({
          text: `Изберете причина за отказ на дигитално подписване на документи`,
          fontSize: '14px',
        });

        dispatch(
          setRejectSignatureRequestDialog({
            open: true,
            title: result1,
            onConfirm: (isRequestRejectedWithReason, rejectSignatureRequestDialog) => {
              endTask({
                data: { ...data },
                taskId,
                closeStatus,
                itsActivityType,
                isTax,
                taxHours,
                taxMinutes,
                taxNote,
                itsActivityDescription,
                closeNote,
                closeReason,
                breakdownCloseReason,
                imageInputData,
                newUsedMaterials,
                dispatch,
                endTaskDialog,
                documentsDashboard,
                isProtocolSignatureRequestRejectedWithReason: isRequestRejectedWithReason,
                rejectProtocolSignatureReasonId:
                  rejectSignatureRequestDialog.rejectSignatureRequestReasonId,
                rejectProtocolSignatureRequestNote:
                  rejectSignatureRequestDialog.rejectSignatureRequestNote,
                sellMaterialsData,
              });
            },
            onCancel: () => {
              dispatch(
                setConfirmationDialog({
                  open: false,
                  title: [],
                  onConfirm: null,
                  onCancel: null,
                })
              );
            },
          })
        );
      }

      const updatedDevicesWithBadIndicators = response?.devicesWithBadIndicators;
      response?.error && dispatch(setSnackbar({ open: true, message: response.error }));

      dispatch(
        setEndTaskDialog({
          ...endTaskDialog,
          isEnding: false,
        })
      );

      if (updatedDevicesWithBadIndicators && updatedDevicesWithBadIndicators.length > 0) {
        const result = [];

        // eslint-disable-next-line array-callback-return
        updatedDevicesWithBadIndicators.map(x => {
          result.push({
            text: `Нивата на устройство ${x?.mac} - не са в норма. Задачата не трябва да бъде затваряна преди тяхната корекция. Моля коригирайте нивата или изберете една от следните причини, ако ситуацията го предполага`,
            fontSize: '14px',
          });
        });
        const updateTasks = onConfirm => {
          const updatedTasks2 = [...data.tasks];
          const updatedTask = updatedTasks2.find(x => x.id === taskId);
          const updatedTaskIndex = updatedTasks2.indexOf(updatedTask);

          if (onConfirm) {
            updatedTask.isAutomaticTestsProceeded = true;
          }
          updatedTasks2.splice(updatedTaskIndex, 1, updatedTask);
          dispatch(
            setData({
              ...data,
              tasks: updatedTasks2.sort((a, b) => sortByDate(a, b)),
            })
          );
          return updatedTasks2;
        };

        dispatch(
          setConfirmationDialog({
            open: true,
            title: result,
            onConfirm: (reasonId, note) => {
              const updatedTasks2 = updateTasks(true);
              endTask({
                data: { ...data, tasks: updatedTasks2 },
                taskId,
                closeStatus,
                itsActivityType,
                isTax,
                taxHours,
                taxMinutes,
                taxNote,
                itsActivityDescription,
                closeNote,
                closeReason,
                breakdownCloseReason,
                imageInputData,
                newUsedMaterials,
                dispatch,
                endTaskDialog,
                automaticTestCloseReasonId: reasonId,
                automaticTestNote: note,
                isContractSignatureRequestRejectedWithReason,
                rejectContractSignatureReasonId,
                rejectContractSignatureRequestNote,
                isProtocolSignatureRequestRejectedWithReason,
                rejectProtocolSignatureReasonId,
                rejectProtocolSignatureRequestNote,
                sellMaterialsData,
              });
            },
            onCancel: () => {
              updateTasks();
              dispatch(
                setConfirmationDialog({
                  open: false,
                  title: [],
                  onConfirm: null,
                  onCancel: null,
                })
              );
              dispatch(setAutomaticTestCloseReasons([]));
            },
          })
        );

        dispatch(setAutomaticTestCloseReasons(response.automaticTestCloseReasons));
        dispatch(setAutomaticTestResults(response?.devicesWithBadIndicators));
        changeConfirmationDialogCss();
      } else {
        dispatch(setAutomaticTestCloseReasons([]));
      }
    }
    dispatch(setTaskVerifyAddress(false));
  })();
};

const updateTask = (data, taskId, changes, dispatch) => {
  const updatedTasks = [...data.tasks];
  const taskIndex = data.tasks.indexOf(data.tasks.find(x => x.id === taskId));
  const task = data.tasks[taskIndex];
  updatedTasks.splice(taskIndex, 1, {
    ...task,
    ...changes,
  });
  dispatch(setData({ ...data, tasks: updatedTasks.sort((a, b) => sortByDate(a, b)) }));
};

const getTicketTypesRequiredForImages = () => [2, 10, 38, 43, 53, 54, 68, 70];

const validateSelectedMaterials = async (
  selectedMaterials,
  ticketId,
  ticketTypeId,
  msisdn,
  dispatch,
  taskEnded
) => {
  if (devicesValidationTicketTypes.includes(ticketTypeId) && selectedMaterials) {
    const response = await asyncFetch(
      `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/validate-selected-materials`,
      {
        method: 'POST',
        mode: 'cors',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify({
          ticketId,
          msisdn,
          materials: selectedMaterials,
        }),
      },
      {
        'Content-Type': 'application/json',
      },
      undefined,
      dispatch
    );
    if (!response?.isSuccess) {
      const mterialsListType = taskEnded
        ? closedTaskUsedMaterialsListType
        : newUsedMaterialsListType;
      response && response.error && dispatch(setSnackbar({ open: true, message: response.error }));
      response.wrongMaterials.map(x => {
        selectedMaterials = removeUsedMaterial(
          x,
          selectedMaterials,
          mterialsListType,
          undefined,
          dispatch
        );
      });
      return false;
    }
  }
  dispatch(setMaterialsDialog({ open: false, dataSet: '', checkout: false }));
  return true;
};

const transferClosedTaskMaterials = (closedTaskUsedMaterials, taskId, data, dispatch) => {
  (async () => {
    const response = await asyncFetch(
      `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/transfer-materials`,
      {
        method: 'POST',
        mode: 'cors',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify({
          taskId,
          materials: closedTaskUsedMaterials,
        }),
      },
      {
        'Content-Type': 'application/json',
      },
      undefined,
      dispatch
    );
    if (response?.isSuccess) {
      // merge closed task used materials into used materials
      const updatedUsedMaterials = [...data.usedMaterials, ...closedTaskUsedMaterials];
      // will probably have to move total substraction here
      const updatedMaterials = [...data.materials];
      for (let i = 0; i < closedTaskUsedMaterials.length; i++) {
        const material = data.materials.find(
          x =>
            x.materialId === closedTaskUsedMaterials[i].materialId &&
            x.serialNumber === closedTaskUsedMaterials[i].serialNumber &&
            x.materialName === closedTaskUsedMaterials[i].materialName
        );
        if (material) {
          const updatedMaterial = { ...material };
          updatedMaterial.quantity -= closedTaskUsedMaterials[i].quantity;
          updatedMaterials.splice(updatedMaterials.indexOf(material), 1, updatedMaterial);
        }
      }

      dispatch(
        setData({ ...data, usedMaterials: updatedUsedMaterials, materials: updatedMaterials })
      );
      dispatch(setClosedTaskUsedMaterials([]));
      dispatch(setSnackbar({ open: true, message: 'Успешно изписани материали' }));
    } else {
      response && response.error && dispatch(setSnackbar({ open: true, message: response.error }));
    }
  })();
};

const validateMaterialToSell = async (data, taskId, dispatch) => {
  const response = await asyncFetch(
    `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/validate-sell-materials`,
    {
      method: 'POST',
      mode: 'cors',
      referrerPolicy: 'strict-origin-when-cross-origin',
      body: JSON.stringify({
        taskId,
        materials: data,
      }),
    },
    {
      'Content-Type': 'application/json',
    },
    undefined,
    dispatch
  );

  if (response?.isSuccess) {
    const serNumbers = data.map(x => x.serialNumber);
    dispatch(
      setSnackbar({ open: true, message: 'Успешно добавяне/премахване на материали за продажба' })
    );
    dispatch(updateMaterialsToSell(serNumbers));
    return true;
  }

  response &&
    dispatch(setSnackbar({ open: true, message: response.error ?? 'Възникна неизвестна грешка' }));
  return false;
};

const validateDuplicatedSerialNumber = (
  selectedMaterials,
  searchInMaterials,
  dispatch,
  isSellMaterialsDialog,
  taskEnded
) => {
  if (taskEnded) return false;
  for (let i = 0; i < selectedMaterials.length; i++) {
    const material = selectedMaterials[i];
    const searchInSerialNumbers = searchInMaterials.map(x => x.serialNumber);
    if (searchInSerialNumbers.includes(material.serialNumber)) {
      dispatch(
        setSnackbar({
          open: true,
          message: `Материал със сериен номер ${material.serialNumber} вече е добавен към ${
            isSellMaterialsDialog ? 'Устройства за влагане' : 'Устройства за продажба'
          }!`,
        })
      );
      return true;
    }
  }
  return false;
};

const getCheckedMaterialsForSell = async (taskId, dispatch) => {
  const checkMaterials = await asyncFetch(
    `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/get-checked-sell-materials?taskId=${taskId}`,
    {
      method: 'GET',
      mode: 'cors',
      referrerPolicy: 'strict-origin-when-cross-origin',
    },
    {
      'Content-Type': 'application/json',
    },
    undefined,
    dispatch
  );

  return checkMaterials;
};

const returnOldDevices = (processedOldDevices, taskId, data, dispatch) => {
  (async () => {
    dispatch(setData({ ...data, isCpeReturnLoading: true }));
    const response = await asyncFetch(
      `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/return-old-devices`,
      {
        method: 'POST',
        mode: 'cors',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify({
          processedOldDevices,
          taskId,
        }),
      },
      {
        'Content-Type': 'application/json',
      },
      undefined,
      dispatch
    );

    if (response && response.isSuccess) {
      dispatch(setSnackbar({ open: true, message: response.successMessage }));

      const updatedTasks = [...data.tasks];

      const updatedTask = updatedTasks.find(x => x.id === taskId);
      updatedTasks.splice(data.tasks.indexOf(updatedTask), 1, {
        ...updatedTask,
        oldDevices: [],
      });

      dispatch(
        setData({
          ...data,
          tasks: updatedTasks,
          isCpeReturnLoading: false,
          processedOldDevices: [],
        })
      );
    } else {
      response && response.error && dispatch(setSnackbar({ open: true, message: response.error }));
      dispatch(setData({ ...data, isCpeReturnLoading: false, processedOldDevices: [] }));
    }
  })();
};

const returnUnknownOldDevice = (taskId, unknownSerialNumber, sapMaterialId, data, dispatch) => {
  (async () => {
    dispatch(setData({ ...data, isCpeReturnLoading: true }));

    const response = await asyncFetch(
      `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/return-unknown-old-device`,
      {
        method: 'POST',
        mode: 'cors',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify({
          taskId,
          serialNumber: unknownSerialNumber,
          sapMaterialId,
        }),
      },
      {
        'Content-Type': 'application/json',
      },
      undefined,
      dispatch
    );

    if (response && response.isSuccess) {
      dispatch(setSnackbar({ open: true, message: response.successMessage }));
    } else {
      response && response.error && dispatch(setSnackbar({ open: true, message: response.error }));
    }

    dispatch(setData({ ...data, isCpeReturnLoading: false }));
  })();
};

const replaceDevice = (
  deviceId,
  newSerialNumber,
  useOldCard,
  newCardSerialNumber,
  taskId,
  data,
  dispatch
) => {
  (async () => {
    dispatch(setData({ ...data, isCpeReturnLoading: true }));

    const response = await asyncFetch(
      `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/replace-device`,
      {
        method: 'POST',
        mode: 'cors',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify({
          deviceId,
          newSerialNumber,
          useOldCard,
          newCardSerialNumber,
          taskId,
        }),
      },
      {
        'Content-Type': 'application/json',
      },
      undefined,
      dispatch
    );

    if (response && response.isSuccess) {
      dispatch(setSnackbar({ open: true, message: response.successMessage }));
    } else {
      response && response.error && dispatch(setSnackbar({ open: true, message: response.error }));
    }

    dispatch(setData({ ...data, isCpeReturnLoading: false }));
  })();
};

const setProcessedOldDevice = (updatedProcessedOldDevice, processedOldDevices, data, dispatch) => {
  const { deviceId } = updatedProcessedOldDevice;

  const oldProcessedOldDevice = processedOldDevices.find(d => d.deviceId === deviceId) || null;
  const updatedProcessedOldDevices = processedOldDevices ? [...processedOldDevices] : [];

  if (oldProcessedOldDevice) {
    const index = updatedProcessedOldDevices.indexOf(oldProcessedOldDevice);

    updatedProcessedOldDevices.splice(index, 1, updatedProcessedOldDevice);
  } else {
    updatedProcessedOldDevices.push(updatedProcessedOldDevice);
  }

  dispatch(setData({ ...data, processedOldDevices: updatedProcessedOldDevices }));
};

const changeConfirmationDialogCss = () => {
  const confirmationDialog = document.getElementsByClassName('ConfirmationDialog');

  for (const element of confirmationDialog) {
    element.setAttribute(
      'style',
      'overflow-y:auto;position:relative;top:100%;width:100%;background-color:#5b6060;'
    );
  }

  const automaticTestCloseReasons = document.getElementsByClassName('AutomaticTestCloseReasons');

  for (const element of automaticTestCloseReasons) {
    element.setAttribute('style', 'width:100%');
  }

  const confirmButton = document.getElementById('ConfirmButton');

  if (confirmButton !== null) {
    confirmButton.setAttribute('class', 'MuiButtonBase-root MuiFab-root Mui-disabled Mui-disabled');
  }
};

const getProtocolSigningUrl = protocolsData => {
  const {
    data,
    task,
    taskIndex,
    documentsDashboard,
    troubleProtocol,
    installationProtocol,
    itsDescriptiveProtocol,
    itsInstalationProtocol,
    itsUninstalProtocol,
    itsInstaledMaterials,
    itsUninstaledMaterials,
    usedMaterials,
    soldDevices,
    shouldPrintAnex,
    dispatch,
  } = protocolsData;

  (async () => {
    let updatedTasks = [...data.tasks];
    updatedTasks.splice(taskIndex, 1, {
      ...task,
      documentsDashboard: { ...documentsDashboard, isLoading: true },
    });

    dispatch(
      setData({
        ...data,
        tasks: updatedTasks.sort((a, b) => sortByDate(a, b)),
      })
    );

    const response = await asyncFetch(
      `${process.env.REACT_APP_TECHNICIANS_API}/Documents/get-protocol-signing-url`,
      {
        method: 'POST',
        mode: 'cors',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify({
          troubleProtocol,
          installationProtocol,
          itsDescriptiveProtocol,
          itsInstalationProtocol,
          itsUninstalProtocol,
          documentsDashboard,
          itsInstaledMaterials,
          itsUninstaledMaterials,
          usedMaterials,
          soldDevices,
          shouldPrintAnex,
          isIts: task.isIts,
        }),
      },
      {
        'Content-Type': 'application/json',
      },
      undefined,
      dispatch
    );

    if (response) {
      if (response.isSuccess) {
        updatedTasks = [...data.tasks];
        updatedTasks.splice(taskIndex, 1, {
          ...task,
          documentsDashboard: {
            ...documentsDashboard,
            open: false,
            protocolSigningUrl: response.protocolSigningUrl,
            isProtocolSignatureRequestPending: true,
            isLoading: false,
          },
        });

        dispatch(
          setData({
            ...data,
            tasks: updatedTasks.sort((a, b) => sortByDate(a, b)),
          })
        );

        window.open(response.protocolSigningUrl, '_blank');
      } else {
        const updatedDocumentDashboard = {
          ...documentsDashboard,
          isLoading: false,
        };

        updatedTasks = [...data.tasks];
        updatedTasks.splice(taskIndex, 1, {
          ...task,
          documentsDashboard: updatedDocumentDashboard,
        });

        dispatch(
          setData({
            ...data,
            tasks: updatedTasks.sort((a, b) => sortByDate(a, b)),
          })
        );
        dispatch(setSnackbar({ open: true, message: response.error }));
      }
    }
    return null;
  })();
};

const getContractSigningUrl = (data, task, taskIndex, documentsDashboard, dispatch) => {
  (async () => {
    let updatedTasks = [...data.tasks];
    updatedTasks.splice(taskIndex, 1, {
      ...task,
      documentsDashboard: { ...documentsDashboard, isLoading: true },
    });

    dispatch(
      setData({
        ...data,
        tasks: updatedTasks.sort((a, b) => sortByDate(a, b)),
      })
    );

    const response = await asyncFetch(
      `${process.env.REACT_APP_TECHNICIANS_API}/Documents/get-contract-signing-url`,
      {
        method: 'POST',
        mode: 'cors',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify({
          documentsDashboard,
        }),
      },
      {
        'Content-Type': 'application/json',
      },
      undefined,
      dispatch
    );

    if (response) {
      if (response.isSuccess) {
        const updatedDocumentDashboard = {
          ...documentsDashboard,
          open: false,
          contractSigningUrl: response.contractSigningUrl,
          isContractSignatureRequestPending: true,
          isLoading: false,
        };
        updatedTasks = [...data.tasks];
        updatedTasks.splice(taskIndex, 1, {
          ...task,
          documentsDashboard: updatedDocumentDashboard,
        });

        dispatch(
          setData({
            ...data,
            tasks: updatedTasks.sort((a, b) => sortByDate(a, b)),
          })
        );

        window.open(response.contractSigningUrl, '_blank');
      } else {
        const updatedDocumentDashboard = {
          ...documentsDashboard,
          isLoading: false,
        };

        updatedTasks = [...data.tasks];
        updatedTasks.splice(taskIndex, 1, {
          ...task,
          documentsDashboard: updatedDocumentDashboard,
        });

        dispatch(
          setData({
            ...data,
            tasks: updatedTasks.sort((a, b) => sortByDate(a, b)),
          })
        );
        dispatch(setSnackbar({ open: true, message: response.error }));
      }
    }
    return null;
  })();
};

const isTicketWithTroubleProtocol = ticketTypeId => {
  return ticketTypesForTroubleProtocol.includes(ticketTypeId);
};

const isTicketWithInstallationProtocol = ticketTypeId => {
  return ticketTypesForInstallationProtocol.includes(ticketTypeId);
};

const getAppData = async ({ data: originalData, dispatch }) => {
  const queryString = '';
  const response = await asyncFetch(
    `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/get-app-data${queryString}`,
    {},
    {},
    () => {
      setTimeout(() => dispatch(setData({ ...originalData, loaded: false }), 3000));
    },
    dispatch
  );

  if (response) {
    if (Object.keys(response).includes('tasks')) {
      const sortedTasks = response.tasks.sort((a, b) => sortByDate(a, b));
      dispatch(setData({ ...response, tasks: sortedTasks, loaded: true, processedOldDevices: [] }));
      const startedTasks = response.tasks.filter(x => x.appStart && !x.appEnd);
      startedTasks.length > 0
        ? dispatch(setTaskStarted({ started: true, taskId: startedTasks[0].id }))
        : dispatch(setTaskStarted({ started: false, taskId: null }));
    } else {
      dispatch(setData({ ...originalData, loaded: true, processedOldDevices: [] }));
    }
  }
};

const dispatchData = (dispatch, data, updatedTasks) => {
  dispatch(
    setData({
      ...data,
      tasks: updatedTasks.sort((a, b) => sortByDate(a, b)),
    })
  );
};

const returnMaterialByQuantity = (serialNumber, deviceId, task, data, dispatch) => {
  (async () => {
    dispatch(setData({ ...data, isReturnMaterialByQuantityLoading: true }));
    const response = await asyncFetch(
      `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/return-material-by-quantity`,
      {
        method: 'POST',
        mode: 'cors',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify({
          serialNumber,
          deviceId,
          taskId: task.id,
        }),
      },
      {
        'Content-Type': 'application/json',
      },
      undefined,
      dispatch
    );

    if (response && response.isSuccess) {
      dispatch(setSnackbar({ open: true, message: response.successMessage }));

      const updatedMaterialsResponse = await asyncFetch(
        `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/get-materials`,
        {},
        {},
        undefined,
        dispatch
      );

      const updatedTasks = [...data.tasks];
      const taskDevices = updatedTasks.find(x => x.id === task.id)?.devices;
      const updatedDevices = taskDevices.filter(x => x.id !== deviceId);

      const updatedTask = updatedTasks.find(x => x.id === task.id);
      updatedTasks.splice(data.tasks.indexOf(updatedTask), 1, {
        ...updatedTask,
        devices: updatedDevices,
      });

      dispatch(
        setData({
          ...data,
          tasks: updatedTasks,
          materials: updatedMaterialsResponse?.materials || data.materials,
          isReturnMaterialByQuantityLoading: false,
        })
      );
    } else {
      response && response.error && dispatch(setSnackbar({ open: true, message: response.error }));
      dispatch(setData({ ...data, isReturnMaterialByQuantityLoading: false }));
    }
  })();
};

const returnMaterialsToWarehouse = (materials, data, dispatch) => {
  (async () => {
    dispatch(setData({ ...data, isReturnToWarehouseLoading: true }));
    const response = await asyncFetch(
      `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/return-materials-to-warehouse`,
      {
        method: 'POST',
        mode: 'cors',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify({
          returnedMaterials: materials,
        }),
      },
      {
        'Content-Type': 'application/json',
      },
      undefined,
      dispatch
    );

    if (response && response.isSuccess) {
      dispatch(setSnackbar({ open: true, message: response.successMessage }));

      const updatedMaterialsResponse = await asyncFetch(
        `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/get-materials`,
        {},
        {},
        undefined,
        dispatch
      );

      dispatch(
        setData({
          ...data,
          materials: updatedMaterialsResponse?.materials || data.materials,
          isReturnToWarehouseLoading: false,
        })
      );
      dispatch(
        setReturnMaterialsDialog({
          open: false,
        })
      );
    } else {
      response && response.error && dispatch(setSnackbar({ open: true, message: response.error }));
      dispatch(setData({ ...data, isReturnToWarehouseLoading: false }));
    }
  })();
};

export {
  getTime,
  groupMaterialsByName,
  getTaskTypeName,
  getTimeSpan,
  asyncFetch,
  formatDate,
  formatDateTime,
  sortByDate,
  addUsedMaterial,
  addReturnedMaterial,
  removeReturnedMaterial,
  startTask,
  endTask,
  updateTask,
  getTicketTypesRequiredForImages,
  transferClosedTaskMaterials,
  returnOldDevices,
  returnUnknownOldDevice,
  deleteUsedMaterial,
  removeUsedMaterial,
  usedMaterialsListType,
  newUsedMaterialsListType,
  closedTaskUsedMaterialsListType,
  compressImages,
  replaceDevice,
  setProcessedOldDevice,
  getProtocolSigningUrl,
  getContractSigningUrl,
  isTicketWithTroubleProtocol,
  isTicketWithInstallationProtocol,
  validateMaterialToSell,
  getCheckedMaterialsForSell,
  getAppData,
  dispatchData,
  validateSelectedMaterials,
  validateDuplicatedSerialNumber,
  returnMaterialByQuantity,
  returnMaterialsToWarehouse,
};
